import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Row } from 'react-bootstrap';
import { Alert, Button, InputGroup } from '@storable/canaveral-components';
import { isDefaultLocale } from 'modules/i18n/locales';
import { BLOG_CATEGORY_PAGE, BLOG_PAGE } from 'shared/pageTypes';
import { isHomePage } from '../../../../../../modules/types';
import { DEFAULT_DOMAIN } from '../../../../../../constants/admin';
import BreadcrumbDisplay from '../../../../../../admin/containers/BreadcrumbDisplay';
import { BUILDER, SEO_PAGES, DRAFT, FACILITY_PAGES, NON_FACILITY_PAGES } from 'shared/abilities';
import { MANAGE } from '../../../../../../modules/authorization/actions';
import useCurrentUser from '../../../../../../admin/hooks/useCurrentUser';
import { syncableOGProperties } from './OGTags';
import { canAccessPage } from './PageSettings';
import UnauthorizedBanner from '../UnauthorizedBanner';
import AdvancedSettings from './AdvancedSettings';
import style from './SinglePageSettings.scss';

const validateSlug = (url, allowPeriods) => {
  const validUrlCharacters = new RegExp(`^[a-z0-9/_${allowPeriods && '\\.'}-]*$`);
  return validUrlCharacters.test(url);
};

const validateUrl = (url) => {
  let [protocol, rest] = url.split('http://');
  if (!protocol.length) return validateSlug(rest, true);

  [protocol, rest] = url.split('https://');
  if (!protocol.length) return validateSlug(rest, true);

  return false;
};

const propTypes = {
  companyName: PropTypes.string.isRequired,
  hostnames: PropTypes.array,
  nonDefaultLanguages: PropTypes.arrayOf(PropTypes.object),
  loadRedirects: PropTypes.func.isRequired,
  makeDraft: PropTypes.func.isRequired,
  page: PropTypes.shape({
    id: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    metadata: PropTypes.shape({
      breadcrumbName: PropTypes.string,
      internalPageName: PropTypes.string,
      title: PropTypes.string,
      titlePostfix: PropTypes.string,
      description: PropTypes.string,
      slug: PropTypes.string,
      type: PropTypes.string.isRequired,
    }).isRequired,
  }),
  savePage: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  updatePage: PropTypes.func.isRequired,
};

const defaultProps = {};

const SinglePageSettings = ({
  companyName,
  hostnames,
  nonDefaultLanguages,
  loadRedirects,
  makeDraft,
  page,
  savePage,
  saving,
  updatePage,
}) => {
  const {
    id,
    children: pageChildren,
    disableEnUsPage: origDisableEnUsPage,
    locale,
    metadata: {
      canonical: origCanonical,
      customUrl: origCustomUrl,
      description: origDescription,
      internalPageName: origInternalPageName,
      metaTags: origMetaTags,
      noIndex: origNoIndex,
      slug: origSlug,
      syncOGTags: origSyncOGTags,
      title: origTitle,
      titlePostfix: origTitlePostfix,
      type,
    },
  } = page;

  const [isValid, setIsValid] = useState();
  const [saved, setSaved] = useState();
  const [error, setError] = useState();
  const [authorized, setAuthorized] = useState();
  const profile = useCurrentUser();

  const localeSuffix = isDefaultLocale(locale) ? '' : `${locale}/`;
  const domain = `${hostnames && hostnames.fqdn ? hostnames.fqdn : DEFAULT_DOMAIN}/${localeSuffix}`;

  const [internalPageName, setInternalPageName] = useState(origInternalPageName || '');
  const updateInternalPageName = e => setInternalPageName(e.target.value);

  const [title, setTitle] = useState(origTitle || '');
  const updateTitle = e => setTitle(e.target.value);

  const [titlePostfix, setTitlePostfix] = useState(origTitlePostfix || '');
  const updateTitlePostfix = e => setTitlePostfix(e.target.value);

  const [description, setDescription] = useState(origDescription || '');
  const updateDescription = e => setDescription(e.target.value);

  const [noIndex, setNoIndex] = useState();
  const toggleNoIndex = () => setNoIndex(!noIndex);

  const [canonical, setCanonical] = useState(origCanonical || '');
  const updateCanonical = e => setCanonical(e.target.value.toLowerCase());
  const [validCanonical, setValidCanonical] = useState();

  const [slug, setSlug] = useState(origSlug || '');
  const [slugMatch, setSlugMatch] = useState();
  // eslint-disable-next-line no-unused-vars
  const [customUrl, setCustomUrl] = useState(origCustomUrl || '');
  const [validCustomUrl, setValidCustomUrl] = useState();

  const [disableEnUsPage, setDisableEnUsPage] = useState();
  const toggleDisableEnUsPage = () => {
    setDisableEnUsPage(!disableEnUsPage);
    setNoIndex(!disableEnUsPage);
  };

  const [syncOGTags, setSyncOGTags] = useState();
  const toggleSyncOGTags = () => setSyncOGTags(!syncOGTags);

  const [metaTags, setMetaTags] = useState();
  const updateMetaTags = metatags => setMetaTags(metatags);

  const [editingBreadcrumb, setEditingBreadcrumb] = useState(false);
  const toggleEditBreadcrumb = () => setEditingBreadcrumb(!editingBreadcrumb);

  const advancedRef = useRef(null);
  const [showAdvancedArea, setAdvancedArea] = useState(false);
  const toggleAdvanced = () => setAdvancedArea(!showAdvancedArea);

  useEffect(() => {
    const canAccessSettings = profile.can(MANAGE, BUILDER, SEO_PAGES);
    const canAccessFacilityPages = profile.can(MANAGE, FACILITY_PAGES);
    const canAccessNonFacilityPages = profile.can(MANAGE, NON_FACILITY_PAGES);
    const canAccessDraft = profile.can(MANAGE, DRAFT);

    setAuthorized(canAccessSettings && canAccessPage({
      canAccessDraft,
      canAccessFacilityPages,
      canAccessNonFacilityPages,
      profile,
      page,
    }));
  }, [page]);

  useEffect(() => {
    if (showAdvancedArea) {
      advancedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showAdvancedArea]);

  const reset = () => {
    setSaved(false);
    setError(null);

    setInternalPageName(origInternalPageName || '');
    setTitle(origTitle);
    setTitlePostfix(origTitlePostfix || companyName);
    setDescription(origDescription || '');
    setNoIndex(origNoIndex);
    setDisableEnUsPage(origDisableEnUsPage);
    setSyncOGTags(origSyncOGTags);
    setMetaTags(origMetaTags);
    setCanonical(origCanonical);
    setSlug(origSlug);
    setValidCustomUrl(validateSlug(origSlug));
    setCustomUrl(origCustomUrl);
    setSlugMatch(false);
  };

  useEffect(reset, [id]);

  useEffect(() => {
    setInternalPageName(origInternalPageName);
  }, [origInternalPageName]);

  useEffect(() => {
    if (syncOGTags) {
      const syncableVars = {
        description,
        title,
      };

      const newTags = [];
      syncableOGProperties.forEach((base) => {
        const newTag = {
          property: base.property,
          content: syncableVars[base.syncableVar],
        };

        const currentTagIdx = metaTags.findIndex(tag => tag.property === newTag.property);

        if (currentTagIdx > -1) {
          newTags[currentTagIdx] = newTag;
        } else {
          newTags.push(newTag);
        }
      });

      setMetaTags(newTags);
    }
  }, [syncOGTags, title, description]);

  useEffect(() => {
    const valid = title && title.length &&
                  description && description.length &&
                  validCanonical &&
                  (isHomePage(type) || (slug && slug.length && validCustomUrl && !slugMatch));

    setIsValid(valid);
    setSaved(false);
  }, [title, description, validCanonical, slug]);

  useEffect(() => {
    const valid = canonical && canonical.length ? validateUrl(canonical) : true;
    setValidCanonical(valid);
  }, [canonical]);

  const onSubmit = async (e) => {
    e.preventDefault();

    setError(null);
    setSaved(false);
    if (!isValid) return;

    const { slug: _, customUrl: __, ...rest } = page.metadata;

    const payload = {
      ...page,
      disableEnUsPage,
      metadata: {
        ...rest,
        internalPageName,
        title,
        titlePostfix,
        description,
        noIndex,
        metaTags,
        canonical,
        syncOGTags,
      },
    };

    if (!isDefaultLocale(page.locale)) {
      delete payload.metadata.internalPageName;
    } else if (disableEnUsPage && page.live) {
      makeDraft(page);
    }

    savePage(payload).then(() => {
      updatePage(payload);

      if (slug !== origSlug) {
        loadRedirects(true);
      }
      setSaved(true);
    }).catch((err) => {
      const msg = err && err.message ? err.message : err;
      setError(msg);
    });
  };

  if (!authorized) {
    return (
      <UnauthorizedBanner>
        You don't have permission to access this page's settings.  If you wish to do so, please contact support.
      </UnauthorizedBanner>
    );
  }

  const advancedSettingsProps = {
    canonical,
    children: pageChildren,
    description,
    disableEnUsPage,
    nonDefaultLanguages,
    metaTags,
    noIndex,
    syncOGTags,
    title,
    togglePageNoIndex: toggleNoIndex,
    toggleLangNoIndex: toggleDisableEnUsPage,
    toggleSyncOGTags,
    updateCanonical,
    updateMetaTags,
    validCanonical,
  };

  return (
    <React.Fragment>
      <div>
        <Row className={style.setting}>
          <label className={style.heading}>
            Internal Page Name
            <InputGroup className={style.input}>
              <InputGroup.Text
                type="text"
                onChange={updateInternalPageName}
                value={internalPageName}
                disabled={!isDefaultLocale(locale)}
              />
            </InputGroup>
          </label>
        </Row>

        <Row className={style.setting}>
          <BreadcrumbDisplay
            id={id}
            toggleEditBreadcrumb={toggleEditBreadcrumb}
            editingBreadcrumb={editingBreadcrumb}
            domain={domain}
          />
        </Row>

        <Row className={style.setting}>
          <label className={style.heading}>
            <span className={style.required}>
              Title
            </span>
            <div className={style.title}>
              <InputGroup className={style.input}>
                <InputGroup.Text
                  type="text"
                  value={title}
                  onChange={updateTitle}
                  className={cx(!title && style.error)}
                />
              </InputGroup>
              <span className={style.separator}>
                |
              </span>
              <InputGroup className={style.input}>
                <InputGroup.Text
                  type="text"
                  value={titlePostfix}
                  onChange={updateTitlePostfix}
                />
              </InputGroup>
            </div>
          </label>
        </Row>

        <Row className={style.setting}>
          <label className={style.heading}>
            <span className={style.required}>
              Page Description
            </span>
            <InputGroup className={style.longInput}>
              <InputGroup.Text
                type="text"
                value={description}
                onChange={updateDescription}
                className={cx(!description && style.error)}
              />
            </InputGroup>
          </label>
        </Row>

        {![BLOG_PAGE, BLOG_CATEGORY_PAGE].includes(type) && (
          <Alert context="dark" className={style.advancedDialog}>
            <div className={style.ready}>Ready to edit advanced settings?</div>
            <div className={style.action} ref={advancedRef}>If you're up for the challenge,
              <Button
                context="bare"
                borderless
                className={style.toggle}
                onClick={toggleAdvanced}
              >
                click here
              </Button>
            </div>
          </Alert>
        )}
        {showAdvancedArea && (
          <AdvancedSettings {...advancedSettingsProps} />
        )}
      </div>
      <div className={style.footer}>
        <div className={style.footerItemsLeft}>
          {!saving && error &&
          <Alert context="danger" className={style.alert}>
            <strong>Error</strong> {error.message || 'An error ocurred'}
          </Alert>
          }
          {saved &&
          <Alert context="success" className={style.alert}>
             Page successfully saved
          </Alert>
          }
        </div>
        <div className={style.footerItemsRight}>
          <Button
            context="neutral"
            onClick={reset}
            className={style.button}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            context="success"
            onClick={onSubmit}
            className={style.button}
            disabled={saving || !isValid}
          >
            {saving ? 'Applying...' : 'Apply'}
          </Button>
        </div>
      </div>
    </React.Fragment>
  );
};

SinglePageSettings.propTypes = propTypes;
SinglePageSettings.defaultProps = defaultProps;

export default SinglePageSettings;
